/* hom: House of Maryam footer layer (sections/hom-footer.liquid, sections/hom-newsletter.liquid).
   Enhancement only: without this file every footer link is visible and the newsletter form still
   posts with the browser's own email check.
   1. Phones (< 750px): each link column's H2 gets a disclosure button; the H2 stays a heading.
   2. "Cookie preferences": revealed once Shopify's cookie banner API (window.privacyBanner) exists,
      and opens its preferences.
   3. Newsletter: email check that accepts capital letters, one submit at a time, and focus on the
      result after Shopify reloads the page at #<form id>, so screen readers announce it. */
(() => {
  const HOM = (window.HOM = window.HOM || {});
  if (HOM.footer) return;

  const phone = window.matchMedia('(max-width: 749px)');

  /* ---------- 1. Link columns ---------- */

  const setColumn = (col) => {
    const heading = col.querySelector('[data-hom-footer-heading]');
    const list = col.querySelector('[data-hom-footer-list]');
    if (!heading || !list || !list.id) return;
    let button = heading.querySelector('.hom-footer__toggle');

    if (phone.matches) {
      if (!button) {
        button = document.createElement('button');
        button.type = 'button';
        button.className = 'hom-footer__toggle hom-focus';
        button.setAttribute('aria-controls', list.id);
        while (heading.firstChild) button.appendChild(heading.firstChild);
        heading.appendChild(button);
        button.addEventListener('click', () => {
          const open = button.getAttribute('aria-expanded') !== 'true';
          col.dataset.homOpen = String(open);
          button.setAttribute('aria-expanded', String(open));
          list.hidden = !open;
        });
      }
      // Closed by default; the column that holds the current page starts open.
      if (!col.dataset.homOpen) {
        col.dataset.homOpen = String(Boolean(list.querySelector('[aria-current="page"]')));
      }
      const open = col.dataset.homOpen === 'true';
      button.setAttribute('aria-expanded', String(open));
      list.hidden = !open;
    } else {
      if (button) {
        while (button.firstChild) heading.insertBefore(button.firstChild, button);
        button.remove();
      }
      list.hidden = false;
    }
    col.setAttribute('data-hom-ready', '');
  };

  const setColumns = (root = document) => {
    root.querySelectorAll('[data-hom-footer-col]').forEach(setColumn);
  };

  const onBreakpoint = () => setColumns();
  if (phone.addEventListener) phone.addEventListener('change', onBreakpoint);
  else if (phone.addListener) phone.addListener(onBreakpoint);

  /* ---------- 2. Cookie preferences ---------- */

  const privacyReady = () =>
    Boolean(window.privacyBanner && typeof window.privacyBanner.showPreferences === 'function');

  const revealCookieLinks = () => {
    if (!privacyReady()) return false;
    document.querySelectorAll('[data-hom-cookie-item]').forEach((item) => {
      item.hidden = false;
    });
    return true;
  };

  let cookieTimer = null;
  const watchPrivacyBanner = () => {
    if (revealCookieLinks() || cookieTimer) return;
    let tries = 0;
    // Shopify loads the banner script on its own schedule; look for it for up to 20 seconds.
    cookieTimer = window.setInterval(() => {
      tries += 1;
      if (revealCookieLinks() || tries >= 40) {
        window.clearInterval(cookieTimer);
        cookieTimer = null;
      }
    }, 500);
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-hom-cookie-preferences]');
    if (!trigger || !privacyReady()) return;
    event.preventDefault();
    window.privacyBanner.showPreferences();
  });
  document.addEventListener('visitorConsentCollected', revealCookieLinks);

  /* ---------- 3. Newsletter ---------- */

  // Any letters (capitals too), one @, a dot in the domain part.
  const EMAIL = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/;

  const setDescribedBy = (input, id, add) => {
    const ids = (input.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
    const next = ids.filter((item) => item !== id);
    if (add) next.unshift(id);
    if (next.length) input.setAttribute('aria-describedby', next.join(' '));
    else input.removeAttribute('aria-describedby');
  };

  const setNewsletter = (root) => {
    const form = root.querySelector('form');
    const input = root.querySelector('[data-hom-newsletter-input]');
    const error = root.querySelector('[data-hom-newsletter-error]');
    const errorText = root.querySelector('[data-hom-newsletter-error-text]');
    const submit = root.querySelector('[data-hom-newsletter-submit]');
    if (!form || !input || !error || !errorText || form.dataset.homReady) return;
    form.dataset.homReady = 'true';
    form.setAttribute('novalidate', '');

    const clearError = () => {
      error.hidden = true;
      error.removeAttribute('role');
      errorText.textContent = '';
      input.removeAttribute('aria-invalid');
      setDescribedBy(input, error.id, false);
    };

    const showError = (message) => {
      input.setAttribute('aria-invalid', 'true');
      setDescribedBy(input, error.id, true);
      error.hidden = false;
      if (document.activeElement !== input) {
        // Focus moves to the field, which reads its label, "invalid" and this message.
        error.removeAttribute('role');
        errorText.textContent = message;
        input.focus();
      } else {
        // Focus is already in the field: announce the message as an alert (cleared first so a
        // repeated message is announced again).
        error.setAttribute('role', 'alert');
        errorText.textContent = '';
        window.requestAnimationFrame(() => {
          errorText.textContent = message;
        });
      }
    };

    form.addEventListener('submit', (event) => {
      const value = input.value.trim();
      if (value !== input.value) input.value = value;
      if (!EMAIL.test(value)) {
        event.preventDefault();
        showError(root.dataset.homErrorInvalid || input.validationMessage || '');
        return;
      }
      if (form.dataset.homSending === 'true') {
        event.preventDefault();
        return;
      }
      form.dataset.homSending = 'true';
      if (submit) submit.setAttribute('aria-disabled', 'true');
    });

    input.addEventListener('input', () => {
      if (input.getAttribute('aria-invalid') === 'true' && EMAIL.test(input.value.trim())) clearError();
    });

    // Result of a post: Shopify returns to this page at #<form id>.
    const formId = form.getAttribute('id');
    if (formId && window.location.hash === `#${formId}`) {
      const success = root.querySelector('[data-hom-newsletter-success]');
      if (success) {
        success.focus();
      } else if (input.getAttribute('aria-invalid') === 'true') {
        input.focus();
      }
    }
  };

  const setNewsletters = (root = document) => {
    root.querySelectorAll('[data-hom-newsletter]').forEach(setNewsletter);
  };

  // Back/forward cache: a page restored after sending must not keep the form locked.
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    document.querySelectorAll('[data-hom-newsletter] form').forEach((form) => {
      delete form.dataset.homSending;
      const submit = form.querySelector('[data-hom-newsletter-submit]');
      if (submit) submit.removeAttribute('aria-disabled');
    });
  });

  /* ---------- Start, and theme editor ---------- */

  const init = (root = document) => {
    setColumns(root);
    setNewsletters(root);
    watchPrivacyBanner();
  };

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const col = event.target.closest && event.target.closest('[data-hom-footer-col]');
    if (!col || !phone.matches) return;
    col.dataset.homOpen = 'true';
    setColumn(col);
  });

  HOM.footer = { refresh: init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }
})();
